import { MetricTarget } from '@bioliberty/lifemind';
import { useCallback } from 'react';
import { useCreateMetricTargetMutation, useDeleteMetricTargetMutation, useUpdateMetricTargetMutation } from '../../api';

interface UseEditMonthlyTargetsProps {
  targets: MetricTarget[];
  formValues: Record<string, number | undefined>;
}

export const useEditMonthlyTargets = ({ targets, formValues }: UseEditMonthlyTargetsProps) => {
  const [createTarget, { isLoading: isCreating }] = useCreateMetricTargetMutation();
  const [updateTarget, { isLoading: isUpdating }] = useUpdateMetricTargetMutation();
  const [deleteTarget, { isLoading: isDeleting }] = useDeleteMetricTargetMutation();

  const isEditing = isCreating || isUpdating || isDeleting;

  const editTargets = useCallback(async () => {
    if (isEditing) {
      return;
    }

    const currentTargets = targets ?? [];
    const currentTargetMap = Object.fromEntries(currentTargets.map((t) => [t.metric, t]));
    const ops: Promise<void>[] = [];

    for (const [metric, value] of Object.entries(formValues)) {
      if (value === undefined || value === null || isNaN(value)) {
        continue;
      }

      const existing = currentTargetMap[metric];
      if (existing) {
        if (existing.value !== value) {
          ops.push(updateTarget({ ...existing, value }).unwrap());
        }
      } else {
        ops.push(createTarget({ metric, value, timespan: 'month' }).unwrap());
      }
    }

    for (const { metric, id } of currentTargets) {
      if (!(metric in formValues) || formValues[metric] === undefined) {
        ops.push(deleteTarget({ id }).unwrap());
      }
    }

    return Promise.all(ops);
  }, [createTarget, deleteTarget, formValues, isEditing, targets, updateTarget]);

  return { editTargets, isEditing };
};
